import { Check, Close, Error as ErrorIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../Button';

// 步骤状态枚举
export enum StepStatus {
    PENDING = 'pending',
    LOADING = 'loading',
    COMPLETED = 'completed',
    ERROR = 'error',
}

// 步骤数据接口
export interface DatStep {
    id: string;
    label: string;
    description: string;
    status: StepStatus;
    error?: string;
}

// 组件Props接口
interface CreateDatStepperModalProps {
    open: boolean;
    onClose: () => void;
    steps: DatStep[];
    currentStepIndex: number;
    isCompleted: boolean;
    onRetry?: () => void;
}

// 样式组件
const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 16px;
        padding: 1rem;
        min-width: 480px;
        max-width: 600px;
        ${({ theme }) => theme.mediaWidth.upToSmall`
        max-width: unset;
        min-width: unset;
        width:100vw;
        margin: 10px;
        padding: 1rem;
        height: calc(100vh - 180px)
        `};
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
    text-align: center;
    font-weight: 600;
    font-size: 1.5rem;
    color: #333;
    padding-bottom: 1rem;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    right: 8px;
    top: 8px;
    color: #666;

    &:hover {
        color: #333;
        background-color: #f5f5f5;
    }
`;

const StyledStepper = styled(Stepper)`
    .MuiStepLabel-root {
        padding: 8px 0;
    }

    .MuiStepContent-root {
        border-left: 2px solid #e0e0e0;
        margin-left: 12px;
        padding-left: 16px;
    }
`;

const StepStatusBox = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
`;

const SuccessBox = styled(Box)`
    text-align: center;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
`;

const SuccessIcon = styled(Check)`
    font-size: 4rem;
    color: #4caf50;
    background: #e8f5e8;
    border-radius: 50%;
    padding: 1rem;
`;

// 步骤状态图标组件
const StepStatusIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
    switch (status) {
        case StepStatus.LOADING:
            return <CircularProgress size={20} />;
        case StepStatus.COMPLETED:
            return <Check sx={{ color: '#4caf50', fontSize: 20 }} />;
        case StepStatus.ERROR:
            return <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />;
        default:
            return null;
    }
};

// 获取步骤图标
const getStepIcon = (
    step: DatStep,
    index: number,
    currentStepIndex: number,
) => {
    if (step.status === StepStatus.COMPLETED) {
        return <Check sx={{ color: '#4caf50' }} />;
    }
    if (step.status === StepStatus.ERROR) {
        return <ErrorIcon sx={{ color: '#f44336' }} />;
    }
    if (step.status === StepStatus.LOADING) {
        return <CircularProgress size={20} />;
    }
    return index + 1;
};

export default function CreateDatStepperModal({
    open,
    onClose,
    steps,
    currentStepIndex,
    isCompleted,
    onRetry,
}: CreateDatStepperModalProps) {
    const history = useHistory();

    const handleClose = () => {
        onClose();
    };

    const handleForceClose = () => {
        // Allow force close at any time
        onClose();
    };

    const handleRetry = () => {
        if (onRetry) {
            onRetry();
        }
    };

    const handleGoToMyLazbubu = () => {
        onClose();
        history.push('/my');
    };

    // 检查是否有错误步骤
    const hasError = steps.some((step) => step.status === StepStatus.ERROR);
    const isNotCompleted = !isCompleted;

    return (
        <StyledDialog
            open={open}
            onClose={() => {}} // 禁用所有默认的关闭行为
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={true} // 禁用ESC键关闭
            BackdropProps={{
                onClick: () => {}, // 禁用点击背景关闭
            }}
        >
            <StyledDialogTitle>
                <span>
                    {isCompleted
                        ? '🎉 Lazbubu Created Successfully!'
                        : 'Creating Lazbubu...'}
                </span>
                <CloseButton onClick={handleForceClose} size="small">
                    <Close />
                </CloseButton>
            </StyledDialogTitle>

            <DialogContent>
                {isCompleted ? (
                    <SuccessBox>
                        <SuccessIcon />
                        <Typography variant="h6" color="#000">
                            Congratulations! Your Lazbubu has been created
                            successfully
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            You can now start interacting with your Lazbubu
                        </Typography>
                        <Button onClick={handleGoToMyLazbubu} size="small">
                            Go to My Lazbubu
                        </Button>
                    </SuccessBox>
                ) : (
                    <StyledStepper
                        activeStep={currentStepIndex}
                        orientation="vertical"
                    >
                        {steps.map((step, index) => (
                            <Step key={step.id}>
                                <StepLabel
                                    icon={getStepIcon(
                                        step,
                                        index,
                                        currentStepIndex,
                                    )}
                                    error={step.status === StepStatus.ERROR}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={500}
                                    >
                                        {step.label}
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {step.description}
                                    </Typography>

                                    {step.status === StepStatus.LOADING && (
                                        <StepStatusBox>
                                            <StepStatusIcon
                                                status={step.status}
                                            />
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                            >
                                                {[
                                                    'sign-and-upload',
                                                    'add-file',
                                                    'add-permission',
                                                    'request-proof',
                                                    'request-reward',
                                                ].includes(step.id)
                                                    ? 'Waiting for wallet confirmation'
                                                    : 'Processing...'}
                                            </Typography>
                                        </StepStatusBox>
                                    )}

                                    {step.status === StepStatus.COMPLETED && (
                                        <StepStatusBox>
                                            <StepStatusIcon
                                                status={step.status}
                                            />
                                            <Typography
                                                variant="body2"
                                                color="success.main"
                                            >
                                                Completed
                                            </Typography>
                                        </StepStatusBox>
                                    )}

                                    {step.status === StepStatus.ERROR &&
                                        step.error && (
                                            <Alert
                                                severity="error"
                                                sx={{ mt: 1 }}
                                            >
                                                {step.error}
                                            </Alert>
                                        )}
                                </StepContent>
                            </Step>
                        ))}
                    </StyledStepper>
                )}

                {/* 重试按钮 - 只在有错误且未完成时显示 */}
                {hasError && isNotCompleted && onRetry && (
                    <Box
                        sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            borderTop: '1px solid #e0e0e0',
                            pt: 2,
                        }}
                    >
                        <Button size="small" onClick={handleRetry}>
                            Retry DAT Creation
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </StyledDialog>
    );
}
